import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Can from '../../auth/Can';
import { listRequestLocations, createRequestLocation, updateRequestLocation, deleteRequestLocation } from '../../api/candidateRequests';
import { pushToast } from '../../components/ui/toastBus';

function LocationFormModal({ open, onClose, location }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setName(location?.name || '');
    setIsActive(location?.is_active ?? true);
    setError('');
  }, [location]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), is_active: isActive };
      return location ? updateRequestLocation(location.id, payload) : createRequestLocation(payload);
    },
    onSuccess: () => {
      pushToast({ type: 'success', message: location ? 'Location updated.' : 'Location created.' });
      queryClient.invalidateQueries({ queryKey: ['request-locations'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.errors?.name?.[0] || err.response?.data?.message || 'Could not save location.'),
  });

  const handleSave = () => {
    if (!name.trim()) {
      setError('Location is required.');
      return;
    }
    setError('');
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={location ? 'Edit location' : 'New location'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Location" value={name} onChange={(e) => setName(e.target.value)} />
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active (offered when updating a request's location)
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

export default function RequestLocationMasters() {
  const queryClient = useQueryClient();
  const [formTarget, setFormTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const query = useQuery({
    queryKey: ['request-locations', { include_inactive: true }],
    queryFn: () => listRequestLocations({ include_inactive: true }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRequestLocation,
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Location deleted.' });
      queryClient.invalidateQueries({ queryKey: ['request-locations'] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete location.' });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: 'name', label: 'Location' },
    { key: 'is_active', label: 'Status', render: (row) => <Badge tone={row.is_active ? 'green' : 'slate'}>{row.is_active ? 'Active' : 'Inactive'}</Badge> },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Can perm="masters.manage">
            <button
              onClick={() => {
                setFormTarget(row);
                setFormOpen(true);
              }}
              className="text-primary hover:underline"
            >
              Edit
            </button>
            <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
              Delete
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Locations"
        description="Places an approved request (e.g. accommodation) can be assigned to."
        actions={
          <Can perm="masters.manage">
            <Button
              onClick={() => {
                setFormTarget(null);
                setFormOpen(true);
              }}
            >
              New Location
            </Button>
          </Can>
        }
      />

      <Table columns={columns} data={query.data || []} isLoading={query.isLoading} emptyMessage="No locations yet." />

      {formOpen && <LocationFormModal open={formOpen} onClose={() => setFormOpen(false)} location={formTarget} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete location"
        message={`Delete "${deleteTarget?.name}"? Requests assigned to it will show no location.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
